import { z } from 'zod';

// Contact form validation schema
export const contactFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(150, 'Name cannot exceed 150 characters')
    .trim(),
  
  role: z
    .string()
    .min(1, 'Role is required')
    .max(150, 'Role cannot exceed 150 characters')
    .trim(),
  
  instituteName: z
    .string()
    .min(1, 'Institute name is required')
    .max(150, 'Institute name cannot exceed 150 characters')
    .trim(),
  
  studentCount: z
    .preprocess((val) => {
      if (val === undefined || val === '') return undefined;
      const n = Number(val);
      return isNaN(n) ? val : n;
    }, z.number().int().min(1, 'Please enter a valid number between 1 and 2,000,000').max(2000000, 'Please enter a valid number between 1 and 2,000,000').optional()),
  
  campuses: z
    .preprocess((val) => {
      if (val === undefined || val === '') return undefined;
      const n = Number(val);
      return isNaN(n) ? val : n;
    }, z.number().int().min(1, 'Please enter a valid number between 1 and 10,000').max(10000, 'Please enter a valid number between 1 and 10,000').optional()),
  message: z.preprocess((val) => {
    if (val === undefined || val === '') return undefined;
    return String(val).trim();
  }, z.string().max(3000, 'Description cannot exceed 3000 characters').optional()),

  contactNumber: z
    .string()
    .min(1, 'Contact number is required')
    .max(20, 'Contact number is too long')
    .refine((val) => {
      // allow digits, spaces, + and - ; require exactly 10 digits
      const digits = (val || '').replace(/\D/g, '');
      return digits.length === 10;
    }, 'Please enter a valid 10-digit contact number'),

  email: z.preprocess((val) => {
    if (val === undefined || val === '') return undefined;
    return String(val).trim();
  }, z.string().email('Please enter a valid email address').max(150).optional()),
 
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

// Helper function to validate form and return formatted errors
export function validateContactForm(data: any): {
  isValid: boolean;
  errors: Record<string, string>;
  data?: ContactFormData;
} {
  try {
    const validatedData = contactFormSchema.parse(data);
    return {
      isValid: true,
      errors: {},
      data: validatedData,
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Record<string, string> = {};
      error.errors.forEach((err) => {
        if (err.path.length > 0) {
          errors[err.path[0] as string] = err.message;
        }
      });
      return {
        isValid: false,
        errors,
      };
    }
    return {
      isValid: false,
      errors: { general: 'Validation failed' },
    };
  }
}
